///
/// EarthBrowser
///
/// Created by Matt Giger on 9/9/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Icons } from "@/components/ui/icons"
import { Github, Loader2, X } from "lucide-react"
import { useState } from "react"

interface Provider {
	id: string
	label: string
	icon: React.ComponentType<{ className?: string }>
	path: string
}

const providers: Provider[] = [
	{ id: "apple", label: "Apple", icon: Icons.apple, path: "/api/webhooks/apple-oauth-url?flow=link" },
	{ id: "google", label: "Google", icon: Icons.googleLogo, path: "/api/webhooks/google-oauth-url?flow=link" },
	{ id: "microsoft", label: "Microsoft", icon: Icons.microsoft, path: "/api/webhooks/microsoft-oauth-url?flow=link" },
	{ id: "github", label: "GitHub", icon: Github, path: "/api/webhooks/github-link-oauth-url" },
]

interface ProviderLinkButtonsProps {
	linkedProviders: Set<string>
	onClose: () => void
}

/**
 * Data-driven list of OAuth providers that can be linked to the account.
 * Fetches the provider's OAuth URL and redirects the browser to it.
 */
export function ProviderLinkButtons({ linkedProviders, onClose }: ProviderLinkButtonsProps) {
	const [loadingProvider, setLoadingProvider] = useState<string | null>(null)

	async function linkAccount(provider: Provider) {
		setLoadingProvider(provider.id)
		try {
			const response = await fetch(`${import.meta.env.VITE_API_URL}${provider.path}`, {
				credentials: 'include'
			})
			const data = await response.json() as { url: string }
			window.location.href = data.url
		} catch (error) {
			console.error(`${provider.label} OAuth error:`, error)
			setLoadingProvider(null)
		}
	}

	return (
		<div className="rounded-lg border border-dashed border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3">
			<div className="flex items-center justify-between mb-2">
				<p className="text-sm font-medium text-muted-foreground">
					Choose a service to link
				</p>
				<Button
					variant="ghost"
					size="sm"
					className="h-6 w-6 p-0 rounded-full"
					onClick={onClose}
				>
					<X className="h-4 w-4" />
				</Button>
			</div>
			<div className="grid grid-cols-1 gap-2">
				{providers.map((provider) => {
					const isLinked = linkedProviders.has(provider.id)
					const isLoading = loadingProvider === provider.id
					const Icon = provider.icon
					return (
						<Button
							key={provider.id}
							disabled={loadingProvider !== null || isLinked}
							onClick={() => linkAccount(provider)}
							variant="outline"
							className="w-full justify-start rounded-lg"
						>
							{isLoading ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<Icon className="mr-2 h-4 w-4" />
							)}
							{isLinked ? `${provider.label} Already Linked` : `Link ${provider.label} Account`}
						</Button>
					)
				})}
			</div>
		</div>
	)
}
